import { useMediaQuery, useTheme } from '@material-ui/core';
import { Backdrop } from '@room-match/shared-ui-components';
import { Header } from 'components/Header';
import { Sidebar } from 'components/Sidebar';
import React, { Suspense, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { setGuardian } from 'shared/redux/slicers/app.slicer';
import { impersonate, setIsLoading, userLogout } from 'shared/redux/slicers/user.slicer';
import { useAppSelector } from 'shared/redux/store';
import { talentService } from 'shared/services/talentService';
import { ls } from 'shared/utils/ls';
import { addBreadcrumb, captureMessage } from '../../sentry';
import { useStyles } from './Layout.style';

type Props = {
  children: React.ReactNode;
};

const { getMyData, getTalentMembers } = talentService();

const Layout: React.FC<Props> = ({ children }) => {
  const classes = useStyles();
  const theme = useTheme();
  const dispatch = useDispatch();
  const screenLarge = useMediaQuery(theme.breakpoints.up('md'), {
    noSsr: true,
  });
  const { app: appState } = useAppSelector((state) => state);
  const { isGuardian, guardianUI } = appState.guardian;

  const [isMobileDrawerOpen, setIsMobileDrawerOpen] = useState(false);
  const [isDrawerCollapse, setIsDrawerCollapse] = useState(!screenLarge);
  const [gToken, setGToken] = useState('');
  const [loading, setLoading] = useState(false);

  const [browserWarningOpen, setBrowserWarningOpen] = useState(true);

  const { getLS, setLS, removeLS } = ls();
  const { id, email } = JSON.parse(getLS('user') || '{}');
  removeLS('agency');
  const { data, isLoading } = getMyData(id);
  const { data: talentMembers, isError, error, isLoading: membersLoading } = getTalentMembers('', gToken);

  useEffect(() => {
    if (data?.data) {
      const { profiles, user_types } = data.data.talent;
      if (user_types.includes('guardian')) {
        const guardianAccount = getLS('user');
        const GToken = getLS('auth_token');
        setGToken(GToken);
        setLS('guardian_token', GToken);
        setLS('guardian_account', guardianAccount);
        if (profiles.length) {
          console.log('isGuardian: true, guardianUI: false');

          dispatch(setGuardian({ isGuardian: true, guardianUI: false }));
        } else {
          console.log('isGuardian: true, guardianUI: true');
          dispatch(setGuardian({ isGuardian: true, guardianUI: true }));
        }
      } else {
        if (localStorage.guardian_token) {
          setGToken(localStorage.guardian_token);
        }
        dispatch(setGuardian({ isGuardian: false, guardianUI: false }));
      }
    }
  }, [data]);

  useEffect(() => {
    if (talentMembers?.data) {
      const impersonateID = getLS('impersonate_id') !== undefined ? getLS('impersonate_id') : '';
      dispatch(setGuardian({ members: talentMembers.data.talents }));

      if (
        (talentMembers.data.talents.length === 1 &&
          isGuardian &&
          getLS('guardian_landing') !== 'true' &&
          localStorage.guardian_token) ||
        localStorage.impersonate_id
      ) {
        setLoading(true);
        setLS('guardian_landing', true);
        setIsLoading(true);
        console.log(
          'dispatching impersonate',
          impersonateID !== '' ? impersonateID : talentMembers.data.talents[0].id,
          impersonateID !== '',
          impersonateID,
          talentMembers.data.talents[0].id,
        );

        dispatch(impersonate(impersonateID !== '' ? impersonateID : talentMembers.data.talents[0].id));
        removeLS('impersonate_id');
      }
    }

    if (isError) {
      if (error.response?.data.errors.refresh_token) {
        dispatch(userLogout());
      }
    }
  }, [talentMembers, error, isGuardian]);

  const handleMobileDrawer = () => {
    setIsMobileDrawerOpen(!isMobileDrawerOpen);
  };

  const toggleDrawerCollapse = () => {
    setIsDrawerCollapse((isCollapse) => !isCollapse);
    console.log(isDrawerCollapse);
  };

  const onClickTestError = () => {
    throw new Error('This is a test error!');
  };

  const onClickTestEvent = () => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    let result = '';
    for (let i = 0; i < 10; i++) {
      result += chars.charAt(Math.floor(Math.random() * chars.length));
    }

    captureMessage({
      message: `Your custom message here: ${result}`,
    });
  };

  const onClickTestBreadcrumbs = () => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    let result = '';
    for (let i = 0; i < 10; i++) {
      result += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    addBreadcrumb({
      category: 'auth',
      message: 'Authenticated user email: ' + email,
      level: 'info',
    });
    captureMessage({
      message: `Your breadcrumb here: ${result}`,
      level: 'info',
    });
  };

  // Check if 'debug=true' is in the URL
  const urlParams = new URLSearchParams(window.location.search);
  const isDebugMode = urlParams.get('debug') === 'true';
  const userAgent = navigator.userAgent.toLowerCase();
  const isNotChrome = !userAgent.includes('chrome') || userAgent.includes('edg');

  if (loading || isLoading || membersLoading) return <Backdrop isLoading={isLoading} />;

  return (
    <div className={classes.root}>
      <Header
        handleDrawerToggle={handleMobileDrawer}
        isDrawerCollapse={isDrawerCollapse}
        browserWarningOpen={browserWarningOpen}
        setBrowserWarningOpen={setBrowserWarningOpen}
      />
      <Sidebar
        handleMobileDrawer={handleMobileDrawer}
        isMobileDrawerOpen={isMobileDrawerOpen}
        isDrawerCollapse={isDrawerCollapse}
        toggleDrawerCollapse={toggleDrawerCollapse}
      />

      <main className={`${classes.content} ${isNotChrome && browserWarningOpen && 'not-chrome'}`}>
        <div className={classes.toolbar} style={{ minHeight: '60px' }} />
        {isDebugMode && (
          <div>
            <button type="button" onClick={onClickTestError}>
              test error
            </button>
            <button type="button" onClick={onClickTestEvent}>
              test event
            </button>
            <button type="button" onClick={onClickTestBreadcrumbs}>
              test breadcrumbs
            </button>
          </div>
        )}
        <Suspense fallback={<Backdrop isLoading={true} />}>{children}</Suspense>
      </main>
    </div>
  );
};

export default Layout;
